import { BellOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Dropdown, Empty, List, Tag, Typography } from "antd";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAuthStore } from "../auth/store";

interface Notification {
  id: string;
  title: string;
  text: string;
  level: "info" | "warning" | "danger" | "success";
  link: string | null;
  created_at: string;
  read_at: string | null;
}

const COLOR: Record<string, string> = { info: "blue", warning: "orange", danger: "red", success: "green" };

/** Bell in the header: unread notifications (personal and by role), refreshed every half minute. */
export function NotificationsBell() {
  const can = useAuthStore((s) => s.can);
  const queryClient = useQueryClient();
  const enabled = can("core.notifications.read");
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get<{ unread: number; items: Notification[] }>("/notifications"),
    enabled,
    refetchInterval: 30_000,
  });
  const read = useMutation({
    mutationFn: (id: string | "all") => api.post(id === "all" ? "/notifications/read-all" : `/notifications/${id}/read`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
  if (!enabled) return null;
  const items = data?.items ?? [];
  const panel = (
    <div style={{ width: 380, maxHeight: 460, overflow: "auto", background: "var(--wms-surface)", boxShadow: "var(--wms-shadow-lg)", border: "1px solid var(--wms-border)", borderRadius: 8, padding: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 8px" }}>
        <Typography.Text strong>Уведомления</Typography.Text>
        {data?.unread ? <Button size="small" type="link" onClick={() => read.mutate("all")}>Прочитать все</Button> : null}
      </div>
      {items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Уведомлений нет" />
      ) : (
        <List
          size="small"
          dataSource={items}
          renderItem={(n) => (
            <List.Item
              style={{ opacity: n.read_at ? 0.6 : 1, cursor: "pointer" }}
              onClick={() => !n.read_at && read.mutate(n.id)}
            >
              <List.Item.Meta
                title={
                  <span>
                    <Tag color={COLOR[n.level] ?? "blue"}>{n.title}</Tag>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{new Date(n.created_at).toLocaleString("ru-RU")}</Typography.Text>
                  </span>
                }
                description={n.link ? <Link to={n.link}>{n.text}</Link> : n.text}
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );
  return (
    <Dropdown popupRender={() => panel} trigger={["click"]} placement="bottomRight">
      <Badge count={data?.unread ?? 0} size="small" offset={[-2, 2]}>
        <Button type="text" icon={<BellOutlined style={{ fontSize: 18 }} />} aria-label="Уведомления" />
      </Badge>
    </Dropdown>
  );
}
